import { useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const GPS_CONSENT_TEXT = "I agree to share my phone's GPS location with Luxury Rideshare while using the app.";
export const GPS_CONSENT_NOTE = "Your location is shared only while the app is open — with your driver during your trip and with Luxury Rideshare. It's required to book.";
export const CARD_AUTH_TEXT = "I authorize Luxury Rideshare to keep my card on file and charge it for items I ask Luxury Rideshare to buy for me.";
export const CARD_AUTH_NOTE = "A valid credit or debit card must stay on file to ride. Each food or alcohol order still asks for your OK.";

const Check = ({ testId, checked, onChange, children }) => (
  <label className="flex cursor-pointer items-start gap-3 text-sm text-zinc-200">
    <input type="checkbox" data-testid={testId} checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-amber-400" />
    <span>{children}</span>
  </label>
);
export { Check as ConsentCheck };

// One-time screen for riders missing GPS consent and/or the standing card authorization.
export default function GpsConsentScreen() {
  const { user, refresh, logout } = useAuth();
  const needGps = !user.gps_consent;
  const needCard = !user.card_authorized;
  const [gps, setGps] = useState(false);
  const [card, setCard] = useState(false);
  const [busy, setBusy] = useState(false);
  const ready = (!needGps || gps) && (!needCard || card);
  const submit = async () => {
    setBusy(true);
    try {
      if (needGps) await api.post("/customer/gps-consent", { agree: true });
      if (needCard) await api.post("/customer/card-authorization", { agree: true });
      await refresh();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a0a0c] px-5" data-testid="gps-consent-screen">
      <div className="lux-card w-full max-w-md p-6 sm:p-8">
        <MapPin className="h-8 w-8 text-amber-400" />
        <h1 className="mt-4 text-2xl font-semibold text-amber-50">Before you ride</h1>
        <p className="mt-2 text-sm text-zinc-400">Please review and accept to continue using Luxury Rideshare.</p>
        <div className="mt-6 space-y-5">
          {needGps && (
            <div data-testid="consent-gps-block">
              <Check testId="gps-consent-checkbox" checked={gps} onChange={setGps}>{GPS_CONSENT_TEXT}</Check>
              <p className="mt-1.5 pl-7 text-xs text-zinc-500">{GPS_CONSENT_NOTE}</p>
            </div>
          )}
          {needCard && (
            <div data-testid="consent-card-block">
              <Check testId="card-authorization-checkbox" checked={card} onChange={setCard}>{CARD_AUTH_TEXT}</Check>
              <p className="mt-1.5 pl-7 text-xs text-zinc-500">{CARD_AUTH_NOTE}</p>
            </div>
          )}
        </div>
        <button data-testid="gps-consent-submit" disabled={!ready || busy} onClick={submit} className="btn-gold mt-6 w-full">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Agree and continue
        </button>
        <button data-testid="gps-consent-signout" onClick={logout} className="mt-4 w-full text-center text-xs text-zinc-500 hover:text-amber-200">Sign out</button>
        <a href="/account" data-testid="gps-consent-delete-account" className="mt-2 block text-center text-xs text-zinc-600 hover:text-red-300">Delete my account instead</a>
      </div>
    </div>
  );
}
